import { InfoIcon, RotateCcwIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { HistoryGroupedSlider } from '~/components/atoms/HistoryGroupedSlider';
import { IconButton } from '~/components/atoms/IconButton';
import { Label } from '~/components/ui/label';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/components/ui/tooltip';
import { useStore } from '~/store';
import { Divider, GroupLabel } from '../toolbar/parts';

const MIN_AREA_MIN = 16;
const MIN_AREA_MAX = 1024;
const MIN_AREA_STEP = 16;

type PinControlsProps = {
  showLabel?: boolean;
};

export function PinControls({ showLabel = true }: PinControlsProps) {
  const blobs = useStore((s) => s.pin.blobs);
  const detectTolerance = useStore((s) => s.pin.detectTolerance);
  const detectMinArea = useStore((s) => s.pin.detectMinArea);
  const detecting = useStore((s) => s.pin.detecting);
  const setDetectTolerance = useStore((s) => s.setDetectTolerance);
  const setDetectMinArea = useStore((s) => s.setDetectMinArea);
  const setBlobs = useStore((s) => s.setBlobs);

  const lastCommittedCountRef = useRef<number | null>(null);
  const [noChangeHint, setNoChangeHint] = useState(false);
  const markCommit = () => {
    lastCommittedCountRef.current = blobs?.length ?? null;
    setNoChangeHint(false);
  };

  useEffect(() => {
    if (detecting) return;
    if (lastCommittedCountRef.current == null) return;
    const current = blobs?.length ?? null;
    if (current == null) return;
    if (current === lastCommittedCountRef.current) {
      setNoChangeHint(true);
    }
  }, [detecting, blobs]);

  const frameCount = blobs?.length ?? 0;
  const isDetected = blobs !== null;

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1.5">
        {showLabel && <GroupLabel>PIN</GroupLabel>}

        <div className="flex items-center gap-1">
          <span className="w-14 text-center tabular-nums text-muted-foreground">
            {detecting ? <DetectingPill /> : isDetected ? `${frameCount} fr` : '…'}
          </span>
          <IconButton
            label="Re-detect frames"
            onClick={() => {
              lastCommittedCountRef.current = null;
              setNoChangeHint(false);
              setBlobs(null);
            }}
          >
            <RotateCcwIcon />
          </IconButton>
        </div>

        <Divider />

        <Label className="text-xs text-muted-foreground whitespace-nowrap">Edge sensitivity</Label>
        <HistoryGroupedSlider
          aria-label="Edge sensitivity"
          className="w-24"
          value={[detectTolerance]}
          min={1}
          max={255}
          step={1}
          onValueChange={([v]) => {
            if (v != null) {
              markCommit();
              setDetectTolerance(v);
            }
          }}
        />
        <span className="w-7 text-right tabular-nums text-muted-foreground">{detectTolerance}</span>
        <Tooltip>
          <TooltipTrigger asChild>
            <InfoIcon className="h-3.5 w-3.5 cursor-help text-muted-foreground/60" />
          </TooltipTrigger>
          <TooltipContent>Lower includes more anti-aliased edge pixels.</TooltipContent>
        </Tooltip>

        <Divider />

        <Label className="text-xs text-muted-foreground whitespace-nowrap">Min size (px²)</Label>
        <HistoryGroupedSlider
          aria-label="Min size (px²)"
          className="w-24"
          value={[detectMinArea]}
          min={MIN_AREA_MIN}
          max={MIN_AREA_MAX}
          step={MIN_AREA_STEP}
          onValueChange={([v]) => {
            if (v != null) {
              markCommit();
              setDetectMinArea(v);
            }
          }}
        />
        <span className="w-9 text-right tabular-nums text-muted-foreground">{detectMinArea}</span>
      </div>
      {noChangeHint && (
        <p className="text-[10px] text-muted-foreground/80">
          No change — try the other slider or Re-detect.
        </p>
      )}
    </div>
  );
}

function DetectingPill() {
  return (
    <span
      role="status"
      aria-live="polite"
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground"
    >
      <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
      Detecting…
    </span>
  );
}
